import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Store, ArrowRight, Sparkles } from 'lucide-react';
import { enterDemo, saveDemoProfile } from '../demo/demoMode';

/* /demo/os — personalized guided demo. Asks who you are, then opens the Admin AI OS
   (synthetic data) or the client Restaurant OS demo. */

export default function DemoEntry() {
  const nav = useNavigate();
  const isEs = (localStorage.getItem('kitxens-lang') || 'en') === 'es';
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [city, setCity] = useState('');

  const go = (dest) => {
    saveDemoProfile({ name: name.trim(), role: role.trim(), city: city.trim() });
    if (dest === 'admin') {
      enterDemo();
      nav('/admin/ai-os', { replace: true });
    } else {
      window.location.href = '/restaurant/demo';
    }
  };

  const input = 'w-full rounded-xl px-4 py-3 text-[14.5px] bg-white/[0.06] border border-white/12 text-white placeholder:text-white/30 outline-none focus:border-[#F77F11] transition-colors';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: '#121418' }} data-testid="demo-entry">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-xl">
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#F77F11] text-center">Kitxens OS · Demo</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold text-white tracking-tight text-center">
          {isEs ? 'Bienvenido. ¿Quién nos visita hoy?' : 'Welcome. Who\u2019s visiting us today?'}
        </h1>
        <p className="mt-2 text-[14px] text-white/55 text-center max-w-md mx-auto">
          {isEs ? 'Personalizamos la demo para ti — datos de muestra, interactivos y 100% anónimos.' : 'We personalize the demo for you — interactive, 100% anonymized sample data.'}
        </p>
        <div className="mt-7 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} className={input} autoFocus
            placeholder={isEs ? 'Tu nombre (ej. María López)' : 'Your name (e.g. Maria Lopez)'} data-testid="demo-name-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={role} onChange={(e) => setRole(e.target.value)} className={input}
              placeholder={isEs ? 'Tu cargo (ej. Dueña, CEO…)' : 'Your role (e.g. Owner, CEO…)'} data-testid="demo-role-input" />
            <input value={city} onChange={(e) => setCity(e.target.value)} className={input}
              placeholder={isEs ? '¿Desde dónde conectas? (ciudad)' : 'Where are you connecting from? (city)'} data-testid="demo-city-input" />
          </div>
        </div>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={() => go('rest')} data-testid="demo-go-restaurant"
            className="group text-left rounded-2xl p-5 bg-white/[0.05] border border-white/12 hover:border-[#F77F11]/60 transition-all hover:-translate-y-0.5">
            <span className="w-10 h-10 rounded-xl bg-[#F77F11]/15 flex items-center justify-center"><Store className="w-5 h-5 text-[#F77F11]" /></span>
            <p className="mt-3 text-[16px] font-bold text-white flex items-center gap-1.5">
              Restaurant AI OS <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </p>
            <p className="mt-1 text-[12.5px] text-white/55 leading-relaxed">
              {isEs ? 'Lo que ve tu cliente: su restaurante con ventas, menú, reseñas y su equipo de IA.' : 'What your client sees: their restaurant with sales, menu, reviews and their AI team.'}
            </p>
          </button>
          <button onClick={() => go('admin')} data-testid="demo-go-admin"
            className="group text-left rounded-2xl p-5 bg-white/[0.05] border border-white/12 hover:border-[#F77F11]/60 transition-all hover:-translate-y-0.5">
            <span className="w-10 h-10 rounded-xl bg-[#F77F11]/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#F77F11]" /></span>
            <p className="mt-3 text-[16px] font-bold text-white flex items-center gap-1.5">
              Kitxens Admin OS <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </p>
            <p className="mt-1 text-[12.5px] text-white/55 leading-relaxed">
              {isEs ? 'El centro de mando completo: revenue, CRM, agentes de IA, finanzas y operaciones.' : 'The full command center: revenue, CRM, AI agents, finance and operations.'}
            </p>
          </button>
        </div>
        <p className="mt-6 text-center text-[11.5px] text-white/35 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> {isEs ? 'Nada de lo que verás es información real.' : 'Nothing you\u2019ll see is real information.'}
        </p>
      </motion.div>
    </div>
  );
}
